import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  IconButton,
  Grid,
  Divider,
  Chip,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import EditIcon from "@mui/icons-material/Edit";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../../../context/UserContext";
import BackBtn from "../../admin/widgets/BackBtn";
import ImageCropperModal from "../../../../widgets/ImageCropperModal";
import { API_ENDPOINT } from "../../../../constants/constants";
import { toast } from "react-toastify";
import { getUser } from "../../../../utility/utility";
const BannerContainer = styled(Box)(({ theme, banner }) => ({
  position: "relative",
  height: 200,
  backgroundColor: theme.palette.grey[300],
  backgroundImage: banner ? `url(${banner})` : "none",
  backgroundSize: "cover",
  backgroundPosition: "center",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

const BannerUploadButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  marginTop: -50,
  marginLeft: theme.spacing(2),
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  border: `4px solid ${theme.palette.background.paper}`,
}));

const ProfileImageUploadButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

const VisitorProfileView = () => {
  const location = useLocation();
  const { user, loading, reloadUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [uploadType, setUploadType] = useState();
  const [cropperOpen, setCropperOpen] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!user) {
    return <Typography>No user data available.</Typography>;
  }

  const handleUpdateProfile = () => {
    navigate("/visitor/edit-profile"); // Redirect to update profile page
  };
  const handleUploadSuccess = (response) => {
    toast.success(response.message);
    reloadUser();
  };

  return (
    <Box>
      <BackBtn />
      <ImageCropperModal
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        aspectRatio={aspectRatio}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}user/upload-profile-image?uploadType=${uploadType}`}
        headers={{
          token: getUser()?.token, //db: testapp
        }}
        onSuccess={handleUploadSuccess}
        onError={(error) => console.error("Upload error:", error)}
      />
      <Box sx={{ maxWidth: 800, mx: "auto", my: 2 }}>
        <Card elevation={3}>
          <BannerContainer banner={user.profileBanner}>
            <Tooltip title="Change Banner">
              <BannerUploadButton
                onClick={() => {
                  setAspectRatio(3);
                  setUploadType("profileBanner");
                  setCropperOpen(true);
                }}
                component="label"
              >
                <PhotoCameraIcon />
              </BannerUploadButton>
            </Tooltip>
          </BannerContainer>

          <ProfileImageContainer>
            <ProfileAvatar src={user.profileImage} alt={user.firstName}>
              {user.firstName?.charAt(0)}
              {user.lastName?.charAt(0)}
            </ProfileAvatar>
            <Tooltip title="Change Profile Image">
              <ProfileImageUploadButton
                onClick={() => {
                  setAspectRatio(1);
                  setUploadType("profileImage");
                  setCropperOpen(true);
                }}
                component="label"
              >
                <PhotoCameraIcon fontSize="small" />
              </ProfileImageUploadButton>
            </Tooltip>
          </ProfileImageContainer>

          <CardContent>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Typography variant="h5" component="div">
                {user.title} {user.firstName} {user.lastName}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={handleUpdateProfile}
              >
                Update Profile
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1">{user.username}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mobile No
                </Typography>
                <Typography variant="body1">{user.mobileNo}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company Name
                </Typography>
                <Typography variant="body1">{user.companyName}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Visitor Group
                </Typography>
                <Typography variant="body1">{user.visitorGroup}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Industry Sector
                </Typography>
                <Typography variant="body1">{user.industrySector}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Location
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img
                    src={user.country.flag}
                    alt={user.country.name}
                    style={{ width: 24, height: 16 }}
                  />
                  <Typography variant="body1">
                    {user.city.name}, {user.state.name}, {user.country.name}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  How did you find out about this event?
                </Typography>
                <Typography variant="body1">
                  {user.howDidYouFindOutAboutThisEvent}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Product Categories
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {user?.productCategories?.map((category, index) => (
                    <Chip
                      key={index}
                      label={`${category.MainCategory}: ${category.Category}`}
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  QR Code
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <img
                    src={user.qrLink}
                    alt="QR Code"
                    style={{ maxWidth: 150, height: "auto" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VisitorProfileView;
